/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import { DSPortal } from '@elliemae/ds-portal';
import { describe, useDeprecateComponent } from '@elliemae/ds-props-helpers';
import { DEFAULT_DELAY_CLOSE, DEFAULT_DELAY_OPEN } from '@elliemae/ds-shared';
import { ThemeContext } from '@elliemae/ds-system';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { DSTooltipArrow } from './DSTooltipArrow.js';
import { TooltipType } from './TooltipType.js';
import type { DSTooltipT } from './index.js';
import { tooltipV2Props } from './propTypes.js';
import {
  StyledAnimatedTooltip,
  StyledTooltipContainer,
  StyledTooltipText,
  StyledTriggerComponentContainer,
} from './styles.js';
import { setMultipleRefs } from './utils/setMultipleRefs.js';
import { tooltipPositions } from './utils/tooltipPositions.js';

const DSTooltipV2 = ({
  containerProps = {},
  innerRef,
  placement = tooltipPositions.BOTTOM,
  title = '',
  delayClose = DEFAULT_DELAY_CLOSE,
  delayOpen = DEFAULT_DELAY_OPEN,
  triggerComponent = null,
  className = '',
  getIsOpen = () => null,
  onOpen = () => null,
  tooltipType = TooltipType.TOOLTIP,
  showArrow = true,
  offset = [0, 14],
  zIndex,
  extraModifiers = {},
}: DSTooltipT): React.ReactElement => {
  useDeprecateComponent({ componentName: 'ds-tooltip-v2', version: 'TBD Date: 2023 Q3' });

  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState(triggerComponent);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const timerOpen = useRef<NodeJS.Timeout>();
  const timerClosed = useRef<NodeJS.Timeout>();

  const { styles, attributes } = usePopper(referenceElement as HTMLElement | null, popperElement, {
    placement,
    modifiers: [
      { name: 'hide', enabled: true },
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: tooltipType === TooltipType.TOOLBAR ? [0, 0] : offset,
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
      { ...extraModifiers },
    ],
  });

  const handleShowTooltipOn = useCallback(() => {
    timerOpen.current = setTimeout(() => {
      setShowTooltip(true);
      setIsAnimating(true);
      getIsOpen(true);
      onOpen();
    }, delayOpen);
  }, [delayOpen, getIsOpen, onOpen]);

  const handleShowTooltipOff = useCallback(() => {
    timerClosed.current = setTimeout(() => {
      setShowTooltip(false);
      setIsAnimating(true);
      getIsOpen(false);
    }, delayClose);
  }, [delayClose, getIsOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      (e.target as HTMLElement).blur();
    }
  }, []);

  const checkAnimationStatus = useCallback(() => {
    if (!showTooltip) {
      setIsAnimating(false);
    }
  }, [showTooltip]);

  useEffect(
    () => () => {
      clearTimeout(timerClosed.current);
      clearTimeout(timerOpen.current);
    },
    [],
  );

  const theme = useContext(ThemeContext);

  return (
    <>
      <StyledTriggerComponentContainer
        innerRef={setReferenceElement as unknown as React.RefObject<HTMLDivElement>}
        onMouseEnter={handleShowTooltipOn}
        onMouseLeave={handleShowTooltipOff}
        onFocus={handleShowTooltipOn}
        onBlur={handleShowTooltipOff}
        onKeyDown={handleKeyDown}
      >
        {triggerComponent}
      </StyledTriggerComponentContainer>
      {isAnimating || showTooltip ? (
        <DSPortal>
          <StyledTooltipContainer
            className={className}
            showTooltip={showTooltip}
            tooltipType={tooltipType}
            data-testid="ds-tooltip-container"
            innerRef={(ref) => setMultipleRefs(setPopperElement, innerRef)(ref)}
            style={{ ...styles.popper, zIndex: zIndex ?? theme.zIndex.tooltip }}
            {...containerProps}
            {...attributes.popper}
          >
            <StyledAnimatedTooltip
              showTooltip={showTooltip}
              data-testid="ds-tooltip"
              onAnimationEnd={checkAnimationStatus}
            >
              <StyledTooltipText>{title}</StyledTooltipText>
              {tooltipType === TooltipType.TOOLTIP && showArrow ? (
                <DSTooltipArrow
                  arrowElementRef={setArrowElement}
                  style={styles.arrow}
                  placement={attributes.popper ? attributes.popper['data-popper-placement'] : 'bottom'}
                />
              ) : null}
            </StyledAnimatedTooltip>
          </StyledTooltipContainer>
        </DSPortal>
      ) : null}
    </>
  );
};

DSTooltipV2.displayName = 'DSTooltipV2';
const TooltipV2WithSchema = describe(DSTooltipV2);
TooltipV2WithSchema.propTypes = tooltipV2Props;

export { DSTooltipV2, TooltipV2WithSchema };
